// 全局数字键盘管理器，用于拦截输入事件并显示数字键盘
import { rlNumericKeypad } from "./rlNumericKeypad";
import { isMobileDevice } from "@/utils/matchMedia";

/**
 * 数字键盘管理器类
 * 用于全局拦截输入框事件并显示自定义数字键盘
 * 仅在移动设备上生效
 */
export class KeypadManager {
  constructor(options = {}) {
    // 单例模式
    if (KeypadManager.instance) {
      return KeypadManager.instance;
    }
    KeypadManager.instance = this;

    // 默认选项
    this.options = {
      selector: options.selector || "", // 默认不使用选择器，改为使用标识符
      enabled: options.enabled !== false, // 是否启用拦截
      keypadOptions: options.keypadOptions || {}, // 数字键盘配置
      excludeSelectors:
        options.excludeSelectors ||
        'input[type="password"], input[type="file"], input[type="checkbox"], input[type="radio"], input[type="hidden"]', // 默认排除的输入类型
      forceInitialize: options.forceInitialize || false, // 是否强制初始化（无论设备类型）
      dataAttributes: options.dataAttributes || [
        "data-use-custom-keypad", // 向后兼容
        "data-numeric-keypad", // 新的统一标识符
        "data-rl-keypad", // 简化版
      ], // 用于标识需要拦截的元素的数据属性
    };

    // 私有属性
    this.keypad = null; // 数字键盘实例
    this.activeElement = null; // 当前激活的输入元素
    this.actualInputElement = null; // 实际的input元素（可能是嵌套在组件内的）
    this.initialized = false; // 是否已初始化
    this.eventHandlers = {}; // 事件处理函数
    this.isMobile = isMobileDevice(); // 是否为移动设备

    // 如果启用且在移动设备上（或强制初始化），立即初始化
    if (
      this.options.enabled &&
      (this.isMobile || this.options.forceInitialize)
    ) {
      this.init();
    }
  }

  /**
   * 初始化管理器
   */
  init() {
    // 如果不是移动设备且未设置强制初始化，则不初始化
    if (!this.isMobile && !this.options.forceInitialize) {
      console.log("非移动设备，不初始化数字键盘");
      return;
    }

    if (this.initialized) return;

    // 创建数字键盘实例
    this.keypad = new rlNumericKeypad({
      ...this.options.keypadOptions,
      // 添加实时同步回调
      onRealTimeSync: true, // 启用实时同步
    });

    // 设置键盘确认回调
    this.keypad.onConfirm((value) => {
      if (this.activeElement) {
        // 值已在键盘内部设置，不需要额外处理
        this.activeElement.blur(); // 失焦元素
      }
    });

    // 设置键盘取消回调
    this.keypad.onCancel(() => {
      if (this.activeElement) {
        this.activeElement.blur(); // 失焦元素
      }
    });

    // 注入CSS样式，帮助阻止iOS上的系统键盘
    this._injectStyles();

    // 绑定全局事件
    this._bindEvents();

    this.initialized = true;
  }

  /**
   * 注入辅助CSS样式
   * @private
   */
  _injectStyles() {
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      /* 防止iOS上系统键盘弹出的特殊技巧 */
      input.rl-keypad-managed {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      /* 禁用iOS的自动调整表单元素样式 */
      @supports (-webkit-touch-callout: none) {
        input.rl-keypad-managed {
          -webkit-appearance: none !important;
        }
      }
      /* Element UI组件样式覆盖 */
      .el-input.rl-keypad-managed .el-input__inner,
      .el-input-number.rl-keypad-managed .el-input__inner {
        cursor: pointer !important;
      }
    `;
    document.head.appendChild(styleElement);
  }

  /**
   * 绑定全局事件
   * @private
   */
  _bindEvents() {
    // 保存事件处理函数引用，方便后续解绑
    this.eventHandlers.mouseDownCapture = this._handleMouseDown.bind(this);
    this.eventHandlers.clickCapture = this._handleClick.bind(this);

    // 使用事件捕获阶段，确保能先于默认行为执行
    document.addEventListener(
      "mousedown",
      this.eventHandlers.mouseDownCapture,
      true
    );
    //这里添加多一个focus的效果
    document.addEventListener("focus", this.eventHandlers.clickCapture, true);
    document.addEventListener("click", this.eventHandlers.clickCapture, true);

    // 特别处理iOS设备（会忽略readOnly属性）
    document.addEventListener("touchend", (e) => {
      // 只处理直接有data-numeric-keypad属性的元素
      if (
        e.target.hasAttribute("data-numeric-keypad") &&
        this.options.enabled
      ) {
        e.target.blur();
      }
    });
  }

  /**
   * 处理鼠标按下事件
   * @param {MouseEvent} e - 事件对象
   * @private
   */
  _handleMouseDown(e) {
    const element = e.target;

    // 使用shouldInterceptElement方法判断是否应该拦截
    if (this.shouldInterceptElement(element) && this.options.enabled) {
      // 阻止默认行为
      e.preventDefault();
      e.stopPropagation();

      // 获取实际的输入元素
      const actualInput = this._getActualInputElement(element);
      if (actualInput) {
        // 应用拦截处理到实际的输入元素
        this._applyInterception(actualInput);
      } else if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement
      ) {
        // 如果找不到实际的输入元素，但元素本身是输入元素，直接应用
        this._applyInterception(element);
      }
      return false;
    }
  }

  /**
   * 处理点击事件
   * @param {MouseEvent} e - 事件对象
   * @private
   */
  _handleClick(e) {
    const element = e.target;
    // 使用shouldInterceptElement方法判断是否应该拦截
    if (this.shouldInterceptElement(element) && this.options.enabled) {
      element.blur();
      // 阻止默认行为
      e.preventDefault();
      e.stopPropagation();

      // 获取实际的输入元素
      const actualInput = this._getActualInputElement(element);
      if (actualInput) {
        // 应用拦截处理到实际的输入元素
        this._applyInterception(actualInput);
      } else if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement
      ) {
        // 如果找不到实际的输入元素，但元素本身是输入元素，直接应用
        this._applyInterception(element);
      }
      return false;
    }
  }

  /**
   * 获取实际的input元素
   * @param {HTMLElement} element - 目标元素
   * @returns {HTMLInputElement|HTMLTextAreaElement|null} - 实际的输入元素
   * @private
   */
  _getActualInputElement(element) {
    if (!element) return null;

    // 如果本身就是input或textarea元素，则直接返回
    if (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement
    ) {
      return element;
    }

    // 如果是VXE相关组件的容器元素，特别处理
    if (
      element.classList &&
      (element.classList.contains("vxe-input") ||
        element.classList.contains("vxe-textarea"))
    ) {
      // 查找内部输入元素
      const input = element.querySelector("input.vxe-input--inner");
      if (input) return input;

      const textarea = element.querySelector("textarea.vxe-textarea--inner");
      if (textarea) return textarea;
    }

    // 如果是VXE表单项
    if (element.classList && element.classList.contains("vxe-form--item")) {
      // 查找表单项内的VXE输入元素
      const input = element.querySelector(".vxe-input--inner");
      if (input) return input;

      const textarea = element.querySelector(".vxe-textarea--inner");
      if (textarea) return textarea;
    }

    // 查找子元素中的input或textarea
    const inputs = element.querySelectorAll("input");
    if (inputs.length > 0) {
      // 优先返回有data-numeric-keypad属性的输入元素
      for (let i = 0; i < inputs.length; i++) {
        if (inputs[i].hasAttribute("data-numeric-keypad")) {
          return inputs[i];
        }
      }

      // 其次优先返回类型为text、number或tel的input
      for (let i = 0; i < inputs.length; i++) {
        const type = inputs[i].type.toLowerCase();
        if (type === "text" || type === "number" || type === "tel") {
          return inputs[i];
        }
      }
      return inputs[0]; // 如果没有特定类型，返回第一个
    }

    // 查找子元素中的textarea
    const textareas = element.querySelectorAll("textarea");
    if (textareas.length > 0) {
      // 优先返回有data-numeric-keypad属性的textarea
      for (let i = 0; i < textareas.length; i++) {
        if (textareas[i].hasAttribute("data-numeric-keypad")) {
          return textareas[i];
        }
      }
      return textareas[0];
    }

    return null;
  }

  /**
   * 判断是否应该拦截元素
   * @param {HTMLElement} element - 待检查的元素
   * @returns {boolean} - 是否应该拦截
   */
  shouldInterceptElement(element) {
    if (!element) return false;

    // 检查是否明确排除
    if (
      element.hasAttribute("data-no-custom-keypad") ||
      element.hasAttribute("data-rl-keypad-disabled")
    ) {
      return false;
    }

    // 检查元素本身是否有数据属性 - 优先使用最精确的匹配
    for (const attr of this.options.dataAttributes) {
      if (element.hasAttribute(attr)) {
        return true;
      }
    }

    // 如果元素是输入元素(input或textarea)，但没有data-numeric-keypad属性，检查更多上下文
    const isInputElement =
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement;

    // 如果是VXE输入元素，检查元素是否有data-numeric-keypad属性
    if (
      element.classList &&
      (element.classList.contains("vxe-input--inner") ||
        element.classList.contains("vxe-textarea--inner"))
    ) {
      // 对于VXE表单输入元素，只有明确带有data-numeric-keypad属性的才拦截
      return element.hasAttribute("data-numeric-keypad");
    }

    // 对于VXE表单组件的父元素，需要更精确的判断
    if (
      element.classList &&
      (element.classList.contains("vxe-form") ||
        element.classList.contains("vxe-form--item"))
    ) {
      // 如果点击的是表单组件但不是具体的输入字段，不拦截
      return false;
    }

    // 检查父元素是否有任何支持的数据属性，限制查找深度
    let parent = element.parentElement;
    let depth = 0;
    const MAX_DEPTH = 3; // 限制查找深度，避免过度匹配

    while (parent && depth < MAX_DEPTH) {
      // 检查父元素是否有VXE相关类名，如果有，只拦截输入元素
      if (
        parent.classList &&
        (parent.classList.contains("vxe-form") ||
          parent.classList.contains("vxe-form--item") ||
          parent.classList.contains("vxe-input") ||
          parent.classList.contains("vxe-textarea"))
      ) {
        // 对于VXE组件，必须是输入元素且有data-numeric-keypad属性
        return isInputElement && element.hasAttribute("data-numeric-keypad");
      }

      for (const attr of this.options.dataAttributes) {
        if (parent.hasAttribute(attr)) {
          // 如果父元素有data-rl-keypad-target属性，检查目标是否匹配当前元素
          const target = parent.getAttribute("data-rl-keypad-target");
          if (target) {
            try {
              return element.matches(target);
            } catch (e) {
              console.warn("Invalid data-rl-keypad-target selector:", target);
            }
          }

          // 对于其他情况，如果是输入元素则拦截
          return isInputElement;
        }
      }
      parent = parent.parentElement;
      depth++;
    }

    // 默认不拦截
    return false;
  }

  /**
   * 应用拦截处理
   * @param {HTMLInputElement|HTMLTextAreaElement} element - 输入元素
   * @private
   */
  _applyInterception(element) {
    // 如果不是移动设备且未设置强制初始化，则不处理
    if (!this.isMobile && !this.options.forceInitialize) {
      return;
    }

    if (!element) return;

    // 确保元素是输入元素
    if (
      !(element instanceof HTMLInputElement) &&
      !(element instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    // 保存元素引用
    this.actualInputElement = element;
    this.activeElement = element;

    // 标记元素为已被管理
    element.classList.add("rl-keypad-managed");

    // 保存原始属性以便后续还原
    const originalReadOnly = element.readOnly;
    const originalInputMode = element.inputMode;

    // 设置各种防止键盘弹出的属性
    element.readOnly = true;
    element.inputMode = "none"; // 告诉浏览器不要显示任何键盘
    element.setAttribute("inputmode", "none");

    // iOS平台的额外处理
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      element.setAttribute("readonly", "readonly"); // 双重保险
    }

    // 设置关联元素并同步当前值
    this.keypad.setTargetElement(element);

    // 显示数字键盘在元素旁边
    this.keypad.showNextToElement(element);

    // 为iOS设备设置一个延迟，然后恢复readOnly状态
    setTimeout(() => {
      if (!originalReadOnly) {
        element.readOnly = false;
      }
      if (originalInputMode) {
        element.inputMode = originalInputMode;
      }
    }, 100);
  }

  /**
   * 检查元素是否属于VXE组件
   * @param {HTMLElement} element - 要检查的元素
   * @returns {boolean} - 是否是VXE组件
   * @private
   */
  _isVxeComponent(element) {
    if (!element) return false;

    // 检查元素自身的类名
    if (
      element.classList &&
      (element.classList.contains("vxe-input--inner") ||
        element.classList.contains("vxe-textarea--inner"))
    ) {
      return true;
    }

    // 检查父元素
    let parent = element.parentElement;
    while (parent) {
      if (
        parent.classList &&
        (parent.classList.contains("vxe-input") ||
          parent.classList.contains("vxe-textarea") ||
          parent.classList.contains("vxe-form--item"))
      ) {
        return true;
      }
      parent = parent.parentElement;
    }

    return false;
  }

  /**
   * 启用拦截
   */
  enable() {
    // 如果不是移动设备且未设置强制初始化，则不启用
    if (!this.isMobile && !this.options.forceInitialize) {
      console.log("非移动设备，不启用数字键盘");
      return;
    }

    this.options.enabled = true;
    if (!this.initialized) {
      this.init();
    }
  }

  /**
   * 禁用拦截
   */
  disable() {
    this.options.enabled = false;
    if (this.keypad) {
      this.keypad.hide();
    }
  }

  /**
   * 销毁管理器
   */
  destroy() {
    // 移除事件监听
    if (this.eventHandlers.mouseDownCapture) {
      document.removeEventListener(
        "mousedown",
        this.eventHandlers.mouseDownCapture,
        true
      );
    }
    if (this.eventHandlers.clickCapture) {
      document.removeEventListener(
        "click",
        this.eventHandlers.clickCapture,
        true
      );
    }

    // 销毁键盘
    if (this.keypad) {
      this.keypad.destroy();
      this.keypad = null;
    }

    // 重置状态
    this.initialized = false;
    this.activeElement = null;
    this.actualInputElement = null;
    KeypadManager.instance = null;
  }

  /**
   * 获取实例（单例模式）
   * @param {object} options - 配置选项
   * @returns {KeypadManager} - 实例
   */
  static getInstance(options = {}) {
    if (!KeypadManager.instance) {
      return new KeypadManager(options);
    }
    return KeypadManager.instance;
  }
}

// 导出单例获取函数
export const getKeypadManager = KeypadManager.getInstance;
